import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CouponInputProps {
  onApply: (code: string, discount: number) => void;
}

export const CouponInput = ({ onApply }: CouponInputProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleApplyCoupon = async () => {
    if (!code) return;
    
    setLoading(true);
    try {
      const { data: coupons, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;

      if (!coupons) {
        toast({
          title: "Invalid coupon",
          description: "Please check your coupon code and try again.",
          variant: "destructive",
        });
        return;
      }

      // Check if coupon is expired
      if (coupons.valid_until && new Date(coupons.valid_until) < new Date()) {
        toast({
          title: "Expired coupon",
          description: "This coupon has expired.",
          variant: "destructive",
        });
        return;
      }

      onApply(code.toUpperCase(), coupons.discount_percentage);
      toast({
        title: "Coupon applied!",
        description: `${coupons.discount_percentage}% discount has been applied to your order.`,
      });
      setCode("");
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast({
        title: "Invalid coupon",
        description: "Please check your coupon code and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-2 mb-4">
      <Input
        placeholder="Enter coupon code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className="uppercase"
      />
      <Button 
        onClick={handleApplyCoupon}
        disabled={!code || loading}
        variant="secondary"
      >
        Apply
      </Button>
    </div>
  );
};